import type { GrantProgram, PriorityProgramOverride } from '@/types/roadmap.types';

/** Same rule as Top Priority Programs: override when present, else award display. */
export function getProgramRealisticFunding(
  program: GrantProgram,
  overrides?: PriorityProgramOverride[],
): string {
  const ovr = overrides?.find((o) => o.program_id === program.id);
  return ovr?.realistic_funding ?? program.award_amount.display;
}

/**
 * Approximate FX for objective-level roll-ups in the deck only (not for compliance or accounting).
 */
/** Published alongside objective roll-ups so footnotes stay in sync with conversion logic. */
export const REALISTIC_FUNDING_DECK_FX = {
  USD_PER_EUR: 1.09,
  USD_PER_PLN: 0.26,
  /** COP per 1 USD (i.e. multiply COP amount by this to get USD). ~1 COP = 0.00027 USD (Apr 2026). */
  USD_PER_COP: 0.00027,
} as const;
const USD_PER_EUR = REALISTIC_FUNDING_DECK_FX.USD_PER_EUR;
const USD_PER_PLN = REALISTIC_FUNDING_DECK_FX.USD_PER_PLN;
const USD_PER_COP = REALISTIC_FUNDING_DECK_FX.USD_PER_COP;

const DASH = '[\u2013\\-]';

function scaleAmount(n: number, unit: string | undefined): number {
  const u = (unit ?? '').toUpperCase();
  if (u === 'K') return n * 1e3;
  if (u === 'M') return n * 1e6;
  return n;
}

function toUsd(amount: number, currency: 'USD' | 'EUR' | 'PLN' | 'COP'): number {
  if (currency === 'USD') return amount;
  if (currency === 'EUR') return amount * USD_PER_EUR;
  if (currency === 'PLN') return amount * USD_PER_PLN;
  return amount * USD_PER_COP;
}

/**
 * Converts `award_amount.max` to USD for deck-level “Funding Potential” sums.
 * COP ceilings are stored as nominal pesos; without conversion they dwarf USD/EUR programs.
 */
export function awardAmountMaxToUsd(program: GrantProgram): number {
  const max = program.award_amount.max;
  if (max == null || max <= 0) return 0;
  const cur = program.currency?.toUpperCase();
  if (cur === 'COP') return max * USD_PER_COP;
  if (cur === 'EUR') return max * USD_PER_EUR;
  if (cur === 'PLN') return max * USD_PER_PLN;
  return max;
}

/**
 * Parse curated realistic_funding strings from roadmap config into a USD band.
 * Returns null when the string does not match a known pattern (that program is skipped in sums).
 */
export function parseRealisticFundingToUsdBand(s: string): { minUsd: number; maxUsd: number } | null {
  const trimmed = s.trim();

  const usdPlus = trimmed.match(new RegExp(`^\\$(\\d+(?:\\.\\d+)?)\\s*M\\s*\\+`, 'i'));
  if (usdPlus) {
    const v = parseFloat(usdPlus[1]) * 1e6;
    return { minUsd: v, maxUsd: v };
  }

  const pln = trimmed.match(
    new RegExp(`PLN\\s*(\\d+(?:\\.\\d+)?)\\s*M\\s*${DASH}\\s*(\\d+(?:\\.\\d+)?)\\s*M`, 'i'),
  );
  if (pln) {
    const min = toUsd(parseFloat(pln[1]) * 1e6, 'PLN');
    const max = toUsd(parseFloat(pln[2]) * 1e6, 'PLN');
    return { minUsd: min, maxUsd: max };
  }

  const copM = trimmed.match(
    new RegExp(`COP\\s*(\\d+(?:\\.\\d+)?)\\s*M\\s*${DASH}\\s*(\\d+(?:\\.\\d+)?)\\s*M`, 'i'),
  );
  if (copM) {
    const min = toUsd(parseFloat(copM[1]) * 1e6, 'COP');
    const max = toUsd(parseFloat(copM[2]) * 1e6, 'COP');
    return { minUsd: min, maxUsd: max };
  }

  const copB = trimmed.match(
    new RegExp(`COP\\s*(\\d+(?:\\.\\d+)?)\\s*B\\s*${DASH}\\s*(\\d+(?:\\.\\d+)?)\\s*B`, 'i'),
  );
  if (copB) {
    const min = toUsd(parseFloat(copB[1]) * 1e9, 'COP');
    const max = toUsd(parseFloat(copB[2]) * 1e9, 'COP');
    return { minUsd: min, maxUsd: max };
  }

  const euroPart = trimmed.replace(/\s*\([^)]*\)\s*$/, '');
  const eur = euroPart.match(
    new RegExp(
      `€(\\d+(?:\\.\\d+)?)\\s*(K|M)?\\s*${DASH}\\s*€(\\d+(?:\\.\\d+)?)\\s*(K|M)?`,
      'i',
    ),
  );
  if (eur) {
    const min = toUsd(scaleAmount(parseFloat(eur[1]), eur[2]), 'EUR');
    const max = toUsd(scaleAmount(parseFloat(eur[3]), eur[4]), 'EUR');
    return { minUsd: min, maxUsd: max };
  }

  const usd = trimmed.match(
    new RegExp(
      `\\$(\\d+(?:\\.\\d+)?)\\s*(K|M)?\\s*${DASH}\\s*\\$(\\d+(?:\\.\\d+)?)\\s*(K|M)?`,
      'i',
    ),
  );
  if (usd) {
    const min = scaleAmount(parseFloat(usd[1]), usd[2]);
    const max = scaleAmount(parseFloat(usd[3]), usd[4]);
    return { minUsd: min, maxUsd: max };
  }

  return null;
}

function formatUsdBand(minUsd: number, maxUsd: number): string {
  const fmt = (x: number) => {
    if (x >= 1_000_000) {
      const m = x / 1_000_000;
      const t = m >= 10 ? m.toFixed(0) : m.toFixed(1);
      return `$${t}M`;
    }
    if (x >= 1_000) return `$${Math.round(x / 1000)}K`;
    return `$${Math.round(x)}`;
  };
  if (Math.abs(minUsd - maxUsd) < 1) return fmt(minUsd);
  return `${fmt(minUsd)}–${fmt(maxUsd)}`;
}

/**
 * Sum of per-program realistic bands in USD for programs in this layer that have priority_overrides.
 * Non-USD amounts use fixed deck-level FX; only programs with parseable override strings contribute.
 */
export function getLayerRealisticFundingUsdSummed(
  programsInLayer: GrantProgram[],
  overrides?: PriorityProgramOverride[],
): string | null {
  if (!overrides?.length) return null;
  const byId = new Map(overrides.map((o) => [o.program_id, o]));
  let sumMin = 0;
  let sumMax = 0;
  let n = 0;
  for (const p of programsInLayer) {
    const o = byId.get(p.id);
    if (!o) continue;
    const band = parseRealisticFundingToUsdBand(o.realistic_funding);
    if (!band) continue;
    sumMin += band.minUsd;
    sumMax += band.maxUsd;
    n++;
  }
  if (n === 0) return null;
  return formatUsdBand(sumMin, sumMax);
}
